import { Box, Text } from "@chakra-ui/react";
import { MDXProvider } from "@mdx-js/react";
import { FC, ReactNode } from "react";

import { CodeBlock } from "./code-block/code-block";
import { DemoEmbed } from "./demo-embed";

import {
  Heading,
  Link,
  PylonLink,
  BtOnly,
  FeatureFlag,
  PremiumDestination,
  APIKeyWarning,
  EventSyncWarning,
  StreamingCutoverWarning,
  SnowflakePasswordDeprecation,
  WIPStub,
  SetSyncSchedule,
  SyncEngineSnippet,
  SourceTestSnippet,
  SyncLogsSnippet,
  ConnectionTypeSnippet,
  EngineComparisonTable,
  WarehouseExport,
  NoCommonErrors,
  MatchRatesExplanation,
  CommonErrorsPreface,
  SyncAlerts,
  LiveDebugger,
  MatchBoosterDestination,
  ArrayFanOutTip,
  TimestampTemplateMapping,
  CreateAPIKeySnippet,
  Strong,
  RowAndColumnOrderingInfo,  UseCaseCard,
  UseCaseGrid,
  ProductFinder,
  WhatsNew,
  WhatsNewItem,
  PushEventSourceSteps,
  PushRegisterAppSteps,
  PushTestSendSteps,
  PushNoActiveDevices,
} from "ui";
import { DownloadLink } from "ui/download-link";
import { Image } from "ui/image";
import { OrderedList, UnorderedList, ListItem } from "ui/list";
import { Table, TD, TH, TR } from "ui/table";
import { Video } from "ui/video";

const components = {
  // Page H1 comes from frontmatter title (see docs.tsx); demote any body-level
  // `#` to h2 so each page has exactly one h1 landmark.
  h1: (props) => <Heading as="h2" {...props} />,
  h2: (props) => <Heading as="h2" {...props} />,
  h3: (props) => <Heading as="h3" {...props} />,
  h4: (props) => <Heading as="h4" {...props} />,
  p: (props) => <Text textStyle="paragraph" {...props} />,
  span: (props) => <Text as="span" textStyle="paragraph" {...props} />,
  strong: (props) => <Strong {...props} />,
  a: (props) => <Link {...props} variant="inline" />,
  img: (props) => {
    return <Image {...props} my={6} />;
  },
  li: ListItem,
  ol: OrderedList,
  ul: UnorderedList,
  table: Table,
  td: TD,
  th: TH,
  tr: TR,
  Heading,
  PylonLink,
  BtOnly,
  FeatureFlag,
  PremiumDestination,
  EventSyncWarning,
  StreamingCutoverWarning,
  SnowflakePasswordDeprecation,
  WIPStub,
  CreateAPIKeySnippet,
  APIKeyWarning,
  SetSyncSchedule,
  SyncEngineSnippet,
  SourceTestSnippet,
  SyncLogsSnippet,
  ConnectionTypeSnippet,
  EngineComparisonTable,
  WarehouseExport,
  NoCommonErrors,
  MatchRatesExplanation,
  CommonErrorsPreface,
  SyncAlerts,
  LiveDebugger,
  MatchBoosterDestination,
  ArrayFanOutTip,
  TimestampTemplateMapping,
  RowAndColumnOrderingInfo,  UseCaseCard,
  UseCaseGrid,
  ProductFinder,
  WhatsNew,
  WhatsNewItem,
  PushEventSourceSteps,
  PushRegisterAppSteps,
  PushTestSendSteps,
  PushNoActiveDevices,
  pre: (props) => <CodeBlock {...props} />,
  code: (props) => {
    if (props.className?.includes("hljs")) {
      // multi-line code
      return (
        <Box
          as="code"
          bg="base.lightBackground"
          border="1px solid"
          borderColor="base.border"
          borderRadius="md"
          color="text.primary"
          fontFamily="mono"
          fontSize="md"
          paddingX={1}
          whiteSpace="pre"
          sx={{
            span: {
              fontSize: "13px",
            },
          }}
          {...props}
        />
      );
    }
    // inline code
    return (
      <Box
        as="code"
        display="inline"
        bg="base.background"
        borderRadius="sm"
        color="text.primary"
        fontFamily="mono"
        fontSize="md"
        paddingX={1}
        paddingY={0.5}
        whiteSpace="pre-wrap"
        {...props}
      />
    );
  },
  CodeNoWrap: (props) => {
    return (
      <Box
        as="code"
        bg="base.lightBackground"
        border="1px solid"
        borderColor="base.border"
        borderRadius="md"
        color="text.primary"
        fontFamily="mono"
        fontSize="sm"
        paddingX={1}
        whiteSpace="pre-wrap"
        {...props}
      />
    );
  },
  Video: ({ poster, source, duration, title, publishedAt }) => {
    return (
      <Video
        poster={poster}
        source={source}
        duration={duration}
        title={title}
        description={
          title +
          ". Get started quickly with this video tutorial from Hightouch Docs."
        }
        uploadDate={publishedAt ?? new Date().toISOString()}
        mb={12}
        transition="150ms all"
      />
    );
  },
  DemoEmbed: ({ title, description, demoId, demoName }) => {
    return (
      <DemoEmbed
        title={title}
        description={description}
        demoId={demoId}
        demoName={demoName}
      />
    );
  },
  DownloadLink: ({ href, fileName, ...props }) => {
    return <DownloadLink href={href} fileName={fileName} {...props} />;
  },
  IntegrationsCount: () => {
    return <Text as="span">{process.env.NEXT_PUBLIC_DESTINATIONS_COUNT}+</Text>;
  },
  SourcesCount: () => {
    return <Text as="span">{process.env.NEXT_PUBLIC_SOURCES_COUNT}+</Text>;
  },
};

export const MDX: FC<Readonly<{ children: ReactNode }>> = ({ children }) => (
  <MDXProvider components={components}>{children}</MDXProvider>
);
